import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Target, Users, Lightbulb, TrendingUp, BookOpen, UserCheck, ArrowRight } from 'lucide-react'
import { fadeIn, staggerContainer } from '../utils/motion'
import Button from '../compenents/Button'
import { useLanguage } from '../context/LanguageContext'
import { useTranslation } from '../utils/translations'

const Services = () => {
  const { language } = useLanguage()
  const t = useTranslation(language)

  const services = [
    {
      icon: Target,
      title: t('home.service1Title'),
      image: 'images/services/Gemini_Generated_Image_5lvegq5lvegq5lve.webp',
      description: t('services.conseil.description'),
      features: [
        t('services.conseil.feature1'),
        t('services.conseil.feature2'),
        t('services.conseil.feature3'),
        t('services.conseil.feature4'),
        t('services.conseil.feature5')
      ],
      link: '/conseil',
      color: 'blue'
    },
    {
      icon: Users,
      title: t('home.service2Title'),
      image: 'images/services/acompeniement.webp',
      description: t('services.accompagnement.description'),
      features: [
        t('services.accompagnement.feature1'),
        t('services.accompagnement.feature2'),
        t('services.accompagnement.feature3'),
        t('services.accompagnement.feature4'),
        t('services.accompagnement.feature5')
      ],
      link: '/accompagnement',
      color: 'orange'
    },
    {
      icon: Lightbulb,
      title: t('home.service3Title'),
      image: 'images/services/Gemini_Generated_Image_8plicw8plicw8pli.webp',
      description: t('services.ai.description'),
      features: [
        t('services.ai.feature1'),
        t('services.ai.feature2'),
        t('services.ai.feature3'),
        t('services.ai.feature4'),
        t('services.ai.feature5')
      ],
      link: '/ai-solutions',
      color: 'blue'
    },
    {
      icon: TrendingUp,
      title: t('home.service4Title'),
      image: 'images/services/Page_Partenariats-et-reseaux-1-2400x1600.webp',
      description: t('services.partnerships.description'),
      features: [
        t('services.partnerships.feature1'),
        t('services.partnerships.feature2'),
        t('services.partnerships.feature3'),
        t('services.partnerships.feature4'),
        t('services.partnerships.feature5')
      ],
      link: '/partnerships',
      color: 'orange'
    },
    {
      icon: BookOpen,
      title: t('services.training.feature1'),
      image: 'images/services/formation.webp',
      description: t('services.training.description'),
      features: [
        t('services.training.feature1'),
        t('services.training.feature2'),
        t('services.training.feature3'),
        t('services.training.feature4'),
        t('services.training.feature5'),
        t('services.training.feature6'),
        t('services.training.feature7'),
        t('services.training.feature8'),
        t('services.training.feature9')
      ],
      link: '/training',
      color: 'blue'
    },
    {
      icon: UserCheck,
      title: t('services.recruitment.feature1'),
      image: 'images/services/recrutement.webp',
      description: t('services.recruitment.description'),
      features: [
        t('services.recruitment.feature1'),
        t('services.recruitment.feature2'),
        t('services.recruitment.feature3'),
        t('services.recruitment.feature4'),
        t('services.recruitment.feature5'),
        t('services.recruitment.feature6')
      ],
      link: '/recruitment',
      color: 'orange'
    }
  ]

  return (
    <div className="pt-26">
      {/* Hero Section avec image de fond */}
      <section className="relative py-20 bg-gradient-to-br from-blue-600/90 to-blue-800/90 text-white overflow-hidden">
        {/* Image de fond */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url("images/services/hero.webp")',
            backgroundBlendMode: 'overlay'
          }}
        >
          {/* Overlay pour assombrir l'image et améliorer la lisibilité */}
          <div className="absolute inset-0 bg-black/40"></div>
        </div>
        
        {/* Pattern subtil */}
        <div className="absolute inset-0 opacity-10 bg-grid-pattern"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="inline-flex items-center px-6 py-3 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 mb-8"
            >
              <TrendingUp size={20} className="mr-2" />
              <span className="font-semibold">{t('services.pageTitle')}</span>
            </motion.div>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-heading font-bold mb-6 leading-tight">
              {t('services.pageTitle')}
            </h1>
            <p className="text-xl md:text-2xl max-w-3xl mx-auto leading-relaxed">
              {t('services.pageSubtitle')}
            </p>

            {/* Stats rapides */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12 max-w-2xl mx-auto"
            >
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold mb-1">6+</div>
                <div className="text-blue-200 text-sm">Services</div>
              </div>
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold mb-1">15+</div>
                <div className="text-blue-200 text-sm">Années Exp.</div>
              </div>
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold mb-1">100+</div>
                <div className="text-blue-200 text-sm">Projets</div>
              </div>
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold mb-1">98%</div>
                <div className="text-blue-200 text-sm">Satisfaction</div>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Vague décorative en bas */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="w-full h-12">
            <path d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z" opacity=".25" className="fill-white"></path>
            <path d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z" opacity=".5" className="fill-white"></path>
            <path d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z" className="fill-white"></path>
          </svg>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
            className="space-y-16"
          >
            {services.map((service, index) => (
              <motion.div
                key={index}
                variants={fadeIn('up', index * 0.1)}
                className={`bg-white rounded-2xl shadow-lg overflow-hidden ${
                  index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                } lg:flex`}
              >
                <div className="lg:w-1/2 p-8 lg:p-12 flex flex-col justify-center">
                  <div className={`inline-flex items-center justify-center w-16 h-16 rounded-xl bg-gradient-to-br ${
                    service.color === 'blue' ? 'from-blue-500 to-blue-700' : 'from-orange-500 to-orange-700'
                  } mb-6`}>
                    <service.icon size={32} className="text-white" />
                  </div>
                  <h2 className="text-3xl font-heading font-bold text-gray-900 mb-4">
                    {service.title}
                  </h2>
                  <p className="text-lg text-gray-600 mb-6">
                    {service.description}
                  </p>
                  <ul className="space-y-3 mb-8">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <span className={`inline-block w-2 h-2 rounded-full mt-2 mr-3 ${
                          service.color === 'blue' ? 'bg-blue-600' : 'bg-orange-600'
                        }`}></span>
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to={service.link}>
                    <Button variant="primary">
                      {t('services.learnMore')}
                      <ArrowRight size={20} className="ml-2" />
                    </Button>
                  </Link>
                </div>
                <div className="lg:w-1/2 h-64 lg:h-auto">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default Services